import React, {useCallback, useEffect, useRef, useState} from "react";
import {KatButton, KatIcon} from "@amzn/katal-react";
import reviewEventBus from "src/app/eventbus/ReviewEventBus";
import {ReviewEvent, ReviewResult, ReviewStatus} from "src/app/constant/Enums";
import {ApiUtil} from "src/app/util/ApiUtil";
import {EEPApi} from "src/app/api/EEPApi";
import {StringUtil} from "src/app/util/StringUtil";
import {DecisionInput} from "src/app/model/DecisionInput";
import OffboardDecisionModal from "src/app/component/odqAndReliability/OffboardDecisionModal";

interface ODQAndReliabilityDecisionFooterProps {
    reviewId: string;
    caseSubType: string;
    country?: string;
    onSLPClick?: () => void;
}

/**
 * ODQAndReliabilityDecisionFooter - Auditor Remarks + decision buttons.
 * ODQ: Offboard | No Action | Create ticket for SLP    Reliability: Offboard | No Action
 *
 * Offboard opens the OffboardDecisionModal (captures reasonCode for both, and rootCause for ODQ)
 * then submits via UpdateReview. No Action is confirmed through the shared ConfirmationDialog.
 * SLP is delegated to the parent (opens the SLP ticket modal).
 */
// SLP escalation is only supported in these markets.
const SLP_SUPPORTED_COUNTRIES = new Set(["US", "CA", "UK", "GB"]);

const ODQAndReliabilityDecisionFooter: React.FC<ODQAndReliabilityDecisionFooterProps> = ({reviewId, caseSubType, country, onSLPClick}) => {
    const commentRef = useRef<HTMLTextAreaElement>(null);
    const [showOffboardModal, setShowOffboardModal] = useState(false);
    const MIN_HEIGHT = 80;
    const MAX_HEIGHT = 400;
    const [textareaHeight, setTextareaHeight] = useState(MIN_HEIGHT);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const startHeight = useRef(MIN_HEIGHT);

    const handleDragMove = useCallback((e: MouseEvent) => {
        if (!isDragging.current) return;
        const delta = startY.current - e.clientY;
        setTextareaHeight(Math.max(MIN_HEIGHT, Math.min(startHeight.current + delta, MAX_HEIGHT)));
    }, []);

    const handleDragEnd = useCallback(() => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
    }, [handleDragMove]);

    const handleDragStart = useCallback((e: React.MouseEvent) => {
        isDragging.current = true;
        startY.current = e.clientY;
        startHeight.current = textareaHeight;
        document.addEventListener('mousemove', handleDragMove);
        document.addEventListener('mouseup', handleDragEnd);
        e.preventDefault();
    }, [textareaHeight, handleDragMove, handleDragEnd]);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
        };
    }, [handleDragMove, handleDragEnd]);

    // No Action is confirmed via the shared ConfirmationDialog, which emits SUBMIT_ODQ_DECISION.
    // A distinct event is used (not SUBMIT_REVIEW) so the generic Review submit listener does not fire.
    useEffect(() => {
        const onConfirmNoAction = () => { submitDecision(ReviewResult.NO_ACTION); };
        reviewEventBus.on(ReviewEvent.SUBMIT_ODQ_DECISION, onConfirmNoAction);
        return () => { reviewEventBus.removeListener(ReviewEvent.SUBMIT_ODQ_DECISION, onConfirmNoAction); };
    }, []);

    const onOffboardClick = () => setShowOffboardModal(true);

    const onNoActionClick = () => reviewEventBus.emit(ReviewEvent.SHOW_REVIEW_CONFIRMATION_DIALOG, true);

    const handleOffboardSubmit = (reasonCode: string, rootCause: string) => {
        setShowOffboardModal(false);
        submitDecision(ReviewResult.OFFBOARDED, reasonCode, rootCause);
    };

    const submitDecision = async (reviewResult: string, reasonCode?: string, rootCause?: string) => {
        const isOffboard = reviewResult === ReviewResult.OFFBOARDED;
        const decision: DecisionInput = {
            reviewResult,
            reviewStatus: ReviewStatus.COMPLETED,
            comment: commentRef.current?.value || "",
            reason: isOffboard ? reasonCode : undefined,
            rootCause: isOffboard && !StringUtil.isNullOrEmpty(rootCause) ? rootCause : undefined,
        };
        try {
            const endTime = new Date().toISOString();
            const request = ApiUtil.createUpdateReviewRequest(reviewId, decision, null, endTime);
            await EEPApi.updateReview(request);
            reviewEventBus.emit(ReviewEvent.CLOSE_REVIEW, true);
        } catch (error: any) {
            const backendMsg = error?.response?.data?.message;
            alert(backendMsg != undefined ? backendMsg
                : "Error while submitting review decision: " + error.message + " Please try again later!");
        }
    };

    const onReturn = () => {
        reviewEventBus.emit(ReviewEvent.CLOSE_REVIEW, true);
    };

    return (
        <div className="katal container inline-display">
            <KatIcon name="keyboard_arrow_left" size="xlarge" onClick={onReturn}
                     className="hover-pointer" data-testid="return-icon" />
            <div className="odq-remarks-wrapper">
                <div className="odq-remarks-header">
                    <label className="odq-remarks-label">Auditor Remarks</label>
                    <span className="odq-remarks-drag-handle" onMouseDown={handleDragStart}
                          title="Drag to resize" data-testid="odq-drag-handle">⋮⋮</span>
                </div>
                <textarea
                    className="odq-remarks-textarea"
                    style={{height: `${textareaHeight}px`}}
                    ref={commentRef}
                    placeholder="Enter remarks..."
                    data-testid="odq-comment"
                />
            </div>
            <div className="inline-display decision-button-div center">
                <KatButton label="Offboard" onClick={onOffboardClick}
                           className="decision-button amazon-orange"
                           data-testid="odq-offboard-btn" />
                <KatButton label="No Action" onClick={onNoActionClick}
                           className="decision-button"
                           data-testid="odq-noaction-btn" />
                {caseSubType === "ODQ" && SLP_SUPPORTED_COUNTRIES.has((country ?? "").toUpperCase()) && (
                    <KatButton label="Create ticket for SLP" onClick={onSLPClick}
                               className="decision-button odq-slp-btn"
                               data-testid="odq-slp-btn" />
                )}
            </div>

            <OffboardDecisionModal visible={showOffboardModal}
                                   caseSubType={caseSubType}
                                   onSubmit={handleOffboardSubmit}
                                   onClose={() => setShowOffboardModal(false)} />
        </div>
    );
};

export default ODQAndReliabilityDecisionFooter;
